import { useQuery } from "@tanstack/react-query";

import { fetchLineage } from "../../api/artifacts";
import { fetchBranches, fetchCommits } from "../../api/versioning";
import { Icons } from "../thread-v4/icons";
import { ThreadV5DagExplorer } from "./ThreadV5DagExplorer";

interface ThreadV5DagExplorerCardProps {
  projectId: string;
  branch: string;
  /** Shared selection — SEEDS the explorer's root, so every DAG view coordinates. */
  selectedNodeId?: string | null;
  /**
   * The DELIBERATE "jump to this row in the table" action (the host's
   * onSelectRun). It CLOSES the focus overlay and switches to the run table, so
   * it is wired to the explorer's `onActivate` (double-click / "open row ↗") —
   * NOT to a single click, which must re-root in-place and never eject.
   */
  onSelect?: (id: string) => void;
  /**
   * Optional pure, non-navigating selection mirror. Fired on every single-click
   * re-root inside the DAG so a host can highlight a row WITHOUT navigating. When
   * omitted the explorer simply self-manages its root.
   */
  onSelectInDag?: (id: string) => void;
  persistedProject?: boolean;
  /** Tile (compact, default 'spine' + expand control) vs expand/full (default 'cone'). */
  compact?: boolean;
  /** Tile-only: open the full DAG focus. */
  onExpand?: () => void;
}

/**
 * Connected wrapper around the ONE unified {@link ThreadV5DagExplorer}. Fetches the
 * project lineage on the shared react-query key (single round-trip across every DAG
 * surface) and feeds it to the explorer, passing the host's selection in/out so the
 * tile, the focus/expand layer, and the full Lineage page are all the SAME view at
 * different sizes. Replaces the three legacy surfaces (ThreadV5DagMinimap,
 * ThreadV5DagFocus, ThreadV5LineageExplorer).
 *
 * It also feeds the Branch view REAL CoW branches/commits from the versioning API
 * (best-effort, full-view only). When a project has no real branch history (e.g. an
 * anon public demo seeded on `main`), the fetch returns nothing and the explorer's
 * Branch panel falls back to its deterministic-from-graph presentation.
 */
export function ThreadV5DagExplorerCard({
  projectId,
  branch,
  selectedNodeId = null,
  onSelect,
  onSelectInDag,
  persistedProject = true,
  compact = false,
  onExpand,
}: ThreadV5DagExplorerCardProps) {
  const { data, status } = useQuery({
    // Same key the legacy minimap/focus used → shared cache, single round-trip.
    queryKey: ["v5-dag-focus", projectId, branch],
    queryFn: () => fetchLineage(projectId, branch, { scope: "all", includePlots: true }),
    staleTime: 60_000,
    enabled: Boolean(projectId) && persistedProject,
  });

  // Real branch/commit history for the Branch view — best-effort (anon viewers or
  // unversioned projects just get the deterministic fallback). Full view only.
  const wantVersioning = !compact && Boolean(projectId) && persistedProject;
  const { data: branchesRaw } = useQuery({
    queryKey: ["v5-branches", projectId],
    queryFn: () => fetchBranches(projectId).catch(() => []),
    enabled: wantVersioning,
    retry: false,
    staleTime: 60_000,
  });
  const { data: commitsRaw } = useQuery({
    queryKey: ["v5-commits", projectId, branch],
    queryFn: () => fetchCommits(projectId, branch, 100).catch(() => []),
    enabled: wantVersioning,
    retry: false,
    staleTime: 60_000,
  });

  const branchHistory = branchesRaw?.length
    ? branchesRaw.map((b) => ({ id: b.name }))
    : undefined;
  const commits = commitsRaw?.length
    ? commitsRaw.map((c) => ({
        hash: c.id.slice(0, 7),
        branch,
        name: c.message,
        date: new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        parents: (c.parent_ids ?? []).map((p) => p.slice(0, 7)),
      }))
    : undefined;

  return (
    <div className={`v5-dag-explorer-host${compact ? " is-compact" : ""}`} data-tour="v5-dag-card">
      <ThreadV5DagExplorer
        nodes={data?.nodes ?? []}
        edges={data?.edges ?? []}
        selectedId={selectedNodeId}
        // A SINGLE CLICK re-roots in-DAG (pure mirror, never navigates); the
        // host's onSelectRun is the DELIBERATE jump on onActivate (double-click /
        // "open row ↗"), so clicking the DAG no longer ejects to the run table.
        onSelect={onSelectInDag}
        onActivate={onSelect}
        compact={compact}
        onExpand={onExpand}
        branchHistory={branchHistory}
        commits={commits}
      />
      {status === "pending" && persistedProject ? (
        <span className="v5-dag-explorer-loading">
          <Icons.Dag size={12} /> loading lineage…
        </span>
      ) : null}
    </div>
  );
}
